import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, type ReactNode } from "react";
import { VIRTUAL_ABOVE } from "../widgets/components/VirtualList";

/**
 * A list whose rows may wrap: drawn in full up to 100 rows, and above that only the rows in view, each measured (lists
 * are virtualised above 100 rows, AGENTS.md).
 */
export function LongList<T>({
  items,
  rowKey,
  row,
  estimate,
  className = "",
  itemClassName = "",
}: {
  items: readonly T[];
  rowKey: (item: T) => string;
  row: (item: T) => ReactNode;
  estimate: number;
  className?: string;
  itemClassName?: string;
}) {
  if (items.length <= VIRTUAL_ABOVE) {
    return (
      <ul className={className}>
        {items.map((item) => (
          <li key={rowKey(item)} className={itemClassName}>
            {row(item)}
          </li>
        ))}
      </ul>
    );
  }
  return <VirtualItems items={items} rowKey={rowKey} row={row} estimate={estimate} itemClassName={itemClassName} />;
}

function VirtualItems<T>({
  items,
  rowKey,
  row,
  estimate,
  itemClassName,
}: {
  items: readonly T[];
  rowKey: (item: T) => string;
  row: (item: T) => ReactNode;
  estimate: number;
  itemClassName: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  // TanStack Virtual returns new functions on every render, which the React Compiler cannot memoise; that is fine.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtual = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => estimate,
    overscan: 8,
  });
  return (
    <div ref={scroller} className="overflow-auto" style={{ maxHeight: "70vh" }}>
      <ul style={{ height: virtual.getTotalSize(), position: "relative" }}>
        {virtual.getVirtualItems().map((v) => {
          const item = items[v.index];
          if (item === undefined) return null;
          return (
            <li
              key={rowKey(item)}
              ref={virtual.measureElement}
              data-index={v.index}
              aria-setsize={items.length}
              aria-posinset={v.index + 1}
              className={`absolute inset-x-0 top-0 ${itemClassName}`}
              style={{ transform: `translateY(${v.start}px)` }}
            >
              {row(item)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A table drawn in full up to 100 rows, and above that only the rows in view, with spacer rows keeping the scroll
 * height. The table owns its scrolling container so the rows can be measured against it; `head` is the caption and
 * header, `cells` returns a row's cells.
 */
export function LongTable<T>({
  head,
  items,
  rowKey,
  cells,
  columns,
  estimate,
  className = "",
  tableClassName = "",
  bodyClassName = "",
}: {
  head: ReactNode;
  items: readonly T[];
  rowKey: (item: T) => string;
  cells: (item: T) => ReactNode;
  columns: number;
  estimate: number;
  className?: string;
  tableClassName?: string;
  bodyClassName?: string;
}) {
  if (items.length <= VIRTUAL_ABOVE) {
    return (
      <div className={className}>
        <table className={tableClassName}>
          {head}
          <tbody className={bodyClassName}>
            {items.map((item) => (
              <tr key={rowKey(item)}>{cells(item)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <VirtualTable
      head={head}
      items={items}
      rowKey={rowKey}
      cells={cells}
      columns={columns}
      estimate={estimate}
      className={className}
      tableClassName={tableClassName}
      bodyClassName={bodyClassName}
    />
  );
}

function VirtualTable<T>({
  head,
  items,
  rowKey,
  cells,
  columns,
  estimate,
  className,
  tableClassName,
  bodyClassName,
}: {
  head: ReactNode;
  items: readonly T[];
  rowKey: (item: T) => string;
  cells: (item: T) => ReactNode;
  columns: number;
  estimate: number;
  className: string;
  tableClassName: string;
  bodyClassName: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtual = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => estimate,
    overscan: 8,
  });
  const shown = virtual.getVirtualItems();
  const top = shown[0]?.start ?? 0;
  const bottom = virtual.getTotalSize() - (shown[shown.length - 1]?.end ?? 0);
  return (
    <div ref={scroller} className={`overflow-auto ${className}`} style={{ maxHeight: "70vh" }}>
      <table className={tableClassName}>
        {head}
        <tbody className={bodyClassName}>
          {top > 0 && (
            <tr aria-hidden="true" style={{ height: top }}>
              <td colSpan={columns} />
            </tr>
          )}
          {shown.map((v) => {
            const item = items[v.index];
            if (item === undefined) return null;
            return (
              <tr key={rowKey(item)} ref={virtual.measureElement} data-index={v.index}>
                {cells(item)}
              </tr>
            );
          })}
          {bottom > 0 && (
            <tr aria-hidden="true" style={{ height: bottom }}>
              <td colSpan={columns} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
